import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useAlerts } from '../../contexts/AlertContext'
import { useAuth } from '../../contexts/AuthContext'
import { contactAPI } from '../../services/api'
import {
  HiOutlineXMark,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlinePaperAirplane,
  HiOutlineChatBubbleLeftRight,
  HiOutlineInbox
} from 'react-icons/hi2'

const ContactModal = ({ isOpen, onClose }) => {
  const { showSuccess, showError } = useAlerts()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('new') // 'new' or 'history'
  const [messageHistory, setMessageHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [formData, setFormData] = useState({
    sender_name: user?.name || '',
    sender_email: user?.email || '',
    sender_phone: user?.mobile || '',
    category: 'contact_user',
    subject: '',
    message: ''
  })

  useEffect(() => {
    if (isOpen && activeTab === 'history') {
      fetchMessageHistory()
    }
  }, [isOpen, activeTab])

  const fetchMessageHistory = async () => {
    try {
      setHistoryLoading(true)
      const response = await contactAPI.getMyContactMessages()
      
      if (response.success) {
        setMessageHistory(response.data.data || [])
      } else {
        showError('Failed to load message history')
      }
    } catch (error) {
      console.error('Error fetching message history:', error)
      showError('Failed to load message history')
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      const response = await contactAPI.submitContactMessage(formData)
      
      if (response.success) {
        showSuccess('Message sent successfully! We\'ll get back to you soon.')
        setFormData({
          sender_name: user?.name || '',
          sender_email: user?.email || '',
          sender_phone: user?.mobile || '',
          category: 'contact_user',
          subject: '',
          message: ''
        })
        onClose()
      } else {
        showError(response.error?.message || 'Failed to send message')
      }
    } catch (error) {
      console.error('Error sending contact message:', error)
      showError('Failed to send message. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      new: { color: 'bg-warning-soft text-warning', label: 'New' },
      in_progress: { color: 'bg-info-soft text-info', label: 'In Progress' },
      replied: { color: 'bg-success-soft text-success', label: 'Replied' },
      closed: { color: 'bg-neutral-soft text-ink-secondary', label: 'Closed' }
    }
    const badge = badges[status] || { color: 'bg-neutral-soft text-ink-secondary', label: status }
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.color}`}>{badge.label}</span>
  }

  if (!isOpen) return null

  // Portal out to <body> so the modal's `position: fixed` is relative to the
  // viewport — not to the Sidebar's transformed wrapper in AppShell (a
  // `transform: translateX(0)` ancestor creates a containing block and breaks
  // `fixed inset-0`, which is why this used to render as a narrow column
  // pinned to the sidebar's width).
  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-elevated text-ink-primary rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border shadow-token-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-subtle sticky top-0 bg-elevated z-10">
          <div>
            <h3 className="text-xl font-semibold text-ink-primary">Contact Support</h3>
            <p className="text-sm text-ink-secondary mt-1">Send us a message or view your message history</p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink-primary p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <HiOutlineXMark className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-subtle bg-canvas/40 sticky top-[88px] z-10">
          <button
            onClick={() => setActiveTab('new')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'new'
                ? 'border-b-2 border-brand text-brand bg-elevated'
                : 'text-ink-secondary hover:text-ink-primary'
            }`}
          >
            <HiOutlinePaperAirplane className="inline w-4 h-4 me-2" />
            New Message
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-b-2 border-brand text-brand bg-elevated'
                : 'text-ink-secondary hover:text-ink-primary'
            }`}
          >
            <HiOutlineInbox className="inline w-4 h-4 me-2" />
            Message History
          </button>
        </div>

        {/* Content */}
        {activeTab === 'new' ? (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-2">
                <HiOutlineUser className="inline w-4 h-4 mr-1" />
                Your Name
              </label>
              <input
                type="text"
                name="sender_name"
                value={formData.sender_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-surface text-ink-primary border border-border rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
                placeholder="Enter your name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-ink-secondary mb-2">
                <HiOutlineEnvelope className="inline w-4 h-4 mr-1" />
                Email Address
              </label>
              <input
                type="email"
                name="sender_email"
                value={formData.sender_email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 bg-surface text-ink-primary border border-border rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              <HiOutlinePhone className="inline w-4 h-4 mr-1" />
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              name="sender_phone"
              value={formData.sender_phone}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-surface text-ink-primary border border-border rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
              placeholder="Enter your phone number"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Message Type
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 bg-surface text-ink-primary border border-border rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
            >
              <option value="contact_user">General Contact</option>
              <option value="bug_report">Bug Report</option>
              <option value="suggestion">Feature Suggestion</option>
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 bg-surface text-ink-primary border border-border rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors"
              placeholder="Brief description of your message"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              className="w-full px-3 py-2 bg-surface text-ink-primary border border-border rounded-lg focus:ring-2 focus:ring-brand/30 focus:border-brand transition-colors resize-none placeholder:text-ink-muted"
              placeholder="Please provide details about your inquiry..."
            />
          </div>

          {/* Info Box */}
          <div className="p-4 bg-info-soft border border-info/30 rounded-lg">
            <p className="text-sm text-info">
              <span className="font-medium">Note:</span> We typically respond within 24-48 hours. For urgent issues, please contact us via phone.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-border-subtle">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-border text-ink-secondary rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <HiOutlinePaperAirplane className="w-4 h-4" />
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>
        ) : (
          /* Message History */
          <div className="p-6">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-brand border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : messageHistory.length === 0 ? (
              <div className="text-center py-12">
                <HiOutlineInbox className="w-16 h-16 text-ink-disabled mx-auto mb-4" />
                <p className="text-ink-secondary">No messages yet</p>
                <p className="text-sm text-ink-muted mt-1">Your message history will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messageHistory.map((msg) => (
                  <div key={msg.id} className="border border-border rounded-lg p-4 hover:bg-muted transition-colors">
                    {/* Message Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-ink-primary">{msg.subject}</h4>
                        <p className="text-xs text-ink-muted mt-1">
                          {new Date(msg.created_at).toLocaleDateString()} at {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      {getStatusBadge(msg.status)}
                    </div>

                    {/* Original Message */}
                    <div className="bg-muted rounded-lg p-3 mb-3">
                      <p className="text-sm text-ink-secondary whitespace-pre-wrap">{msg.message}</p>
                    </div>

                    {/* Reply (if exists) */}
                    {msg.reply_text && (
                      <div className="border-s-4 border-brand bg-brand-soft/40 rounded-e-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <HiOutlineChatBubbleLeftRight className="w-4 h-4 text-brand" />
                          <span className="text-sm font-medium text-brand">Support Reply</span>
                          {msg.replied_at && (
                            <span className="text-xs text-ink-muted">
                              {new Date(msg.replied_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-ink-secondary whitespace-pre-wrap">{msg.reply_text}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export default ContactModal

